import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/adminAPI';
import { usePermissions, PermissionGate } from '../hooks/usePermissions';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import './UserManagement.css';

function UserManagement() {
  const { hasPermission } = usePermissions();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState({
    user_id: '',
    password: '',
    name: '',
    email: '',
    role: 'user',
    is_active: true,
  });

  useEffect(() => {
    fetchUsers();
  }, [page, search, roleFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await adminAPI.getUsers({
        page,
        per_page: 20,
        search,
        role: roleFilter,
      });

      setUsers(response.data.users);
      setTotalPages(response.data.total_pages);
    } catch (err) {
      setError('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleRoleFilter = (e) => {
    setRoleFilter(e.target.value);
    setPage(1);
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({
      user_id: '',
      password: '',
      name: '',
      email: '',
      role: 'user',
      is_active: true,
    });
    setShowModal(true);
  };

  const openEditModal = (user) => {
    // Prevent regular admins from editing other admin or super_admin accounts
    if (currentUser?.role === 'admin') {
      if (user.role === 'admin' || user.role === 'super_admin') {
        setError('Admins cannot modify other admin or super admin accounts');
        setTimeout(() => setError(''), 3000);
        return;
      }
      // Prevent admins from editing their own role
      if (user.id === currentUser.id) {
        setError('Admins cannot modify their own account role');
        setTimeout(() => setError(''), 3000);
        return;
      }
    }
    
    setEditingUser(user);
    setFormData({
      user_id: user.user_id,
      password: '',
      name: user.name,
      email: user.email || '',
      role: user.role,
      is_active: user.is_active,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingUser) {
        await adminAPI.updateUser(editingUser.id, {
          name: formData.name,
          email: formData.email,
          role: formData.role,
          is_active: formData.is_active,
        });
        setSuccess('User updated successfully');
      } else {
        // Creating new user - password will be auto-generated
        if (!formData.user_id || !formData.name || !formData.email) {
          setError('User ID, name, and email are required');
          return;
        }
        
        await adminAPI.createUser({
          user_id: formData.user_id,
          name: formData.name,
          email: formData.email,
          role: formData.role,
        });
        setSuccess('User created successfully. A temporary password has been sent to their email.');
      }

      closeModal();
      fetchUsers();

      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save user');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    // Parse UTC timestamp with Z suffix and convert to local time
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true
    });
  };

  const openResetPasswordModal = (user) => {
    setResetPasswordUser(user);
    setNewPassword('');
    setShowResetPasswordModal(true);
    setError('');
  };

  const closeResetPasswordModal = () => {
    setShowResetPasswordModal(false);
    setResetPasswordUser(null);
    setNewPassword('');
    setError('');
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      // No need to send password - backend will auto-generate it
      await adminAPI.resetUserPassword(resetPasswordUser.id);
      setSuccess(`Password reset successfully for ${resetPasswordUser.user_id}. A temporary password has been sent to their email. User will be required to change it on next login.`);
      closeResetPasswordModal();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    }
  };

  const handleDeleteUser = async (user) => {
    // Only super_admin can delete users
    if (currentUser?.role !== 'super_admin') {
      setError('Only Super Admins can delete users');
      return;
    }

    // Cannot delete yourself
    if (user.id === currentUser.id) {
      setError('Cannot delete your own account');
      return;
    }

    const confirmMessage = `Are you sure you want to delete user "${user.user_id}" (${user.name})?\n\nThis action cannot be undone. The user will be permanently removed from the system.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await adminAPI.deleteUser(user.id);
      setSuccess(response.data.message || `User ${user.user_id} deleted successfully`);
      fetchUsers(); // Refresh the list
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete user');
      setTimeout(() => setError(''), 5000);
    }
  };

  const canResetPassword = (targetUser) => {
    if (!currentUser) return false;
    
    const currentRole = currentUser.role || 'user';

    // Super admin can reset any password
    if (currentRole === 'super_admin') {
      return true;
    }

    // Admin can reset their own password or regular user passwords
    if (currentRole === 'admin') {
      // Can reset own password
      if (targetUser.user_id === currentUser.userId) {
        return true;
      }
      // Can reset regular user passwords (not other admins or super admins)
      if (targetUser.role === 'user') {
        return true;
      }
      return false;
    }

    // Regular users cannot reset passwords
    return false;
  };

  return (
    <div className="app-container">
      <Header />
      <Navigation activeTab="admin" />

      <div className="content">
        <div className="page-header">
          <h1 className="page-title">User Management</h1>
          <PermissionGate permission="create_users">
            <button className="btn-create-user" onClick={openCreateModal}>
              Create New User
            </button>
          </PermissionGate>
        </div>

        {error && (
          <div className="error-message">
            {error}
            <button onClick={() => setError('')}>×</button>
          </div>
        )}

        {success && (
          <div className="success-message">
            {success}
            <button onClick={() => setSuccess('')}>×</button>
          </div>
        )}

        <div className="filters-bar">
          <input
            type="text"
            placeholder="Search by user ID, name, or email..."
            value={search}
            onChange={handleSearch}
            className="search-input"
          />
          <select value={roleFilter} onChange={handleRoleFilter} className="filter-select">
            <option value="">All Roles</option>
            <option value="user">User</option>
            <option value="admin">Admin</option>
            <option value="super_admin">Super Admin (IT)</option>
          </select>
        </div>

        {loading ? (
          <div className="loading">Loading users...</div>
        ) : (
          <>
            <div className="table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>User ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Last Login</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="loading-state">
                        <div className="spinner"></div>
                        <p>Loading users...</p>
                      </td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="empty-state">
                        <div className="empty-state-content">
                          <p className="empty-state-title">No users found</p>
                          <p className="empty-state-text">
                            {search || roleFilter
                              ? 'Try adjusting your filters'
                              : 'No users have been created yet'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <tr key={user.id}>
                        <td>{user.user_id}</td>
                        <td>{user.name}</td>
                        <td>{user.email || '-'}</td>
                        <td>
                          <span className={`badge-role ${user.role}`}>{user.role}</span>
                        </td>
                        <td>
                          <span
                            className={`badge-status ${user.is_active ? 'active' : 'inactive'}`}
                          >
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>{formatDate(user.last_login)}</td>
                        <td>{formatDate(user.created_at)}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <PermissionGate permission="edit_users">
                              <button className="btn-edit" onClick={() => openEditModal(user)}>
                                Edit
                              </button>
                              {canResetPassword(user) && (
                                <button
                                  className="btn-reset-password"
                                  onClick={() => openResetPasswordModal(user)}
                                >
                                  Reset Password
                                </button>
                              )}
                              {currentUser?.role === 'super_admin' && 
                               user.id !== currentUser.id && (
                                <button
                                  className="btn-delete"
                                  onClick={() => handleDeleteUser(user)}
                                  title="Delete user (Super Admin only)"
                                >
                                  Delete
                                </button>
                              )}
                            </PermissionGate>
                            {!hasPermission('edit_users') && (
                              <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                                No permission
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn-page"
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="btn-page"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingUser ? 'Edit User' : 'Create New User'}</h2>
                <button className="modal-close" onClick={closeModal}>
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>
                    User ID <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="user_id"
                    value={formData.user_id}
                    onChange={handleInputChange}
                    disabled={editingUser !== null}
                    required
                  />
                </div>

                {!editingUser && (
                  <div className="alert alert-info" style={{ margin: '15px 0', padding: '12px', backgroundColor: '#e3f2fd', border: '1px solid #2196f3', borderRadius: '4px' }}>
                    <strong>Password:</strong> A secure random password will be automatically generated and sent to the user's email address. The user will be required to change this password on their first login.
                  </div>
                )}

                <div className="form-group">
                  <label>
                    Full Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>
                    Email <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                  {!editingUser && (
                    <small style={{ color: '#666', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                      The temporary password will be sent to this email address
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>Role</label>
                  <select name="role" value={formData.role} onChange={handleInputChange}>
                    <option value="user">User</option>
                    {/* Only super_admin can assign admin or super_admin roles */}
                    {currentUser?.role === 'super_admin' && (
                      <>
                        <option value="admin">Admin</option>
                        <option value="super_admin">Super Admin (IT)</option>
                      </>
                    )}
                  </select>
                </div>

                {editingUser && (
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                      />
                      Active
                    </label>
                  </div>
                )}

                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingUser ? 'Update User' : 'Create User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showResetPasswordModal && (
          <div className="modal-overlay" onClick={closeResetPasswordModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Reset Password</h2>
                <button className="modal-close" onClick={closeResetPasswordModal}>
                  ×
                </button>
              </div>

              {error && (
                <div className="error-message" style={{ margin: '15px 0' }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleResetPassword}>
                <div className="form-group">
                  <label>User</label>
                  <input
                    type="text"
                    value={`${resetPasswordUser?.user_id} - ${resetPasswordUser?.name}`}
                    disabled
                    style={{ backgroundColor: '#f5f5f5' }}
                  />
                </div>

                <div className="alert alert-info" style={{ margin: '15px 0' }}>
                  <strong>How it works:</strong>
                  <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                    <li>A secure random password will be automatically generated</li>
                    <li>The password will be sent to the user's email: <strong>{resetPasswordUser?.email}</strong></li>
                    <li>User will be required to change this password on their next login</li>
                  </ul>
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={closeResetPasswordModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    Generate & Send Password
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserManagement;
